import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";

const STORAGE_KEY = "slotique:map-auto-search";
const AUTO_SEARCH_PREF_EVENT = "map-auto-search-changed";

function readAutoSearchPref(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeAutoSearchPref(value: boolean) {
  try {
    if (value) {
      localStorage.setItem(STORAGE_KEY, "1");
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
    window.dispatchEvent(new Event(AUTO_SEARCH_PREF_EVENT));
  } catch {
    // localStorage unavailable
  }
}

function subscribeMapAutoSearchPref(onStoreChange: () => void): () => void {
  const handler = () => onStoreChange();
  window.addEventListener(AUTO_SEARCH_PREF_EVENT, handler);
  return () => window.removeEventListener(AUTO_SEARCH_PREF_EVENT, handler);
}

interface SearchAreaButtonProps {
  visible: boolean;
  autoSearch: boolean;
  onAutoSearchChange: (value: boolean) => void;
  onClick: () => void;
  className?: string;
}

// fallow-ignore-next-line complexity
function SearchAreaButton({
  visible,
  autoSearch,
  onAutoSearchChange,
  onClick,
  className,
}: SearchAreaButtonProps) {
  const prefersReducedMotion = useReducedMotion();

  return (
    <div
      className={cn(
        "absolute top-4 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2",
        className,
      )}
    >
      <AnimatePresence>
        {visible && !autoSearch && (
          <motion.div
            initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
            transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.2 }}
          >
            <Button variant="primary" size="sm" onClick={onClick} className="shadow-pop">
              <Search className="size-3.5" />
              Search this area
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      <label
        className={cn(
          "flex cursor-pointer items-center gap-2 rounded-pill bg-surface/90 px-3 py-1.5 text-[12px] font-medium text-ink shadow-soft2 backdrop-blur-sm select-none",
          autoSearch && "ring-1 ring-electric/30",
        )}
      >
        <input
          type="checkbox"
          checked={autoSearch}
          onChange={(e) => onAutoSearchChange(e.target.checked)}
          className="sr-only"
        />
        <span
          className={cn(
            "inline-block size-3.5 rounded-xs border-2 transition-colors",
            autoSearch ? "border-electric bg-electric" : "border-slate/40 bg-transparent",
          )}
          aria-hidden="true"
        >
          {autoSearch && (
            <svg
              viewBox="0 0 14 14"
              fill="none"
              className="size-full"
              aria-hidden="true"
              role="presentation"
            >
              <path
                d="M3 7l3 3 5-5"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </span>
        Search as I move
      </label>
    </div>
  );
}

export { readAutoSearchPref, SearchAreaButton, subscribeMapAutoSearchPref, writeAutoSearchPref };
